import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  CreateMasterBuild,
  CreateOrder,
  EntityDto,
  MasterBuild,
  MasterBuildService,
  Order,
  OrderChange,
  PagedMasterBuildHistoryRequest,
  PagedMasterBuildOrderRequest,
  PagedMasterBuildRequest,
  UpdateMasterBuild,
} from '@services';

class MasterBuildStore {
  /** Current page of the Master Builds table. */
  builds: Map<string, MasterBuild> = new Map();
  /** The build open on the details page. Its jobs are paged separately, below. */
  build: MasterBuild | undefined;
  skipCount: number = 0;
  currentPage: number = 1;
  totalCount: number = 0;
  loading: boolean = false;
  saving: boolean = false;
  maxResultCount: number = 15;

  /**
   * One page of the open build's jobs. A restoration can carry well over a hundred, so they
   * are fetched and filtered on the server rather than coming down with the build.
   */
  orders: Map<string, Order> = new Map();
  ordersTotalCount: number = 0;
  ordersSkipCount: number = 0;
  ordersCurrentPage: number = 1;
  ordersMaxResultCount: number = 10;
  ordersLoading: boolean = false;

  /**
   * Which fetch is the current one. Typing in the filter and then turning a page starts two
   * requests, and the second is not guaranteed to answer second - without this the slower,
   * older answer overwrites the newer one and the reader sees a page they did not ask for.
   * Deliberately not observable: it is bookkeeping, nothing renders from it.
   */
  ordersRequestId: number = 0;

  /**
   * The build's unified history log - every job's changes, ordered by when they happened. Its own
   * page state, separate from the jobs list above: the two tabs are read independently.
   */
  history: OrderChange[] = [];
  historyTotalCount: number = 0;
  historySkipCount: number = 0;
  historyCurrentPage: number = 1;
  historyMaxResultCount: number = 20;
  historyLoading: boolean = false;

  constructor() {
    makeAutoObservable(this, { ordersRequestId: false });
  }

  async getOrders(request: PagedMasterBuildOrderRequest) {
    this.ordersRequestId += 1;
    const requestId = this.ordersRequestId;

    this.setOrdersLoading(true);
    // Emptied before the request, not after it. Clearing on arrival left the previous page on
    // screen for the whole round trip and then swapped it out under the reader.
    runInAction(() => this.orders.clear());

    try {
      const result = await MasterBuildService.getOrders(request);

      // A newer request has already been made; this answer is stale and would undo it.
      if (requestId !== this.ordersRequestId) return;

      runInAction(() => {
        result.items.forEach((order) => this.orders.set(order.id, order));
        this.ordersTotalCount = result.totalCount;
      });
    } catch (error) {
      console.error(error);
    } finally {
      // Only the newest request may put the spinner away, or it disappears while one is running.
      if (requestId === this.ordersRequestId) this.setOrdersLoading(false);
    }
  }

  setOrdersLoading = (value: boolean) => {
    runInAction(() => {
      this.ordersLoading = value;
    });
  };

  setOrdersPage = (current: number) => {
    runInAction(() => {
      this.ordersCurrentPage = current;
      this.ordersSkipCount = (current - 1) * this.ordersMaxResultCount;
    });
  };

  setOrdersPageSize = (size: number) => {
    runInAction(() => {
      this.ordersMaxResultCount = size;
      this.ordersCurrentPage = 1;
      this.ordersSkipCount = 0;
    });
  };

  clearOrders = () => {
    runInAction(() => {
      this.orders.clear();
      this.ordersTotalCount = 0;
      this.ordersSkipCount = 0;
      this.ordersCurrentPage = 1;
    });
  };

  async getHistory(request: PagedMasterBuildHistoryRequest) {
    this.setHistoryLoading(true);
    try {
      const result = await MasterBuildService.getHistory(request);
      runInAction(() => {
        this.history = result.items;
        this.historyTotalCount = result.totalCount;
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.setHistoryLoading(false);
    }
  }

  setHistoryLoading = (value: boolean) => {
    runInAction(() => {
      this.historyLoading = value;
    });
  };

  setHistoryPage = (current: number) => {
    runInAction(() => {
      this.historyCurrentPage = current;
      this.historySkipCount = (current - 1) * this.historyMaxResultCount;
    });
  };

  setHistoryPageSize = (size: number) => {
    runInAction(() => {
      this.historyMaxResultCount = size;
      this.historyCurrentPage = 1;
      this.historySkipCount = 0;
    });
  };

  clearHistory = () => {
    runInAction(() => {
      this.history = [];
      this.historyTotalCount = 0;
      this.historySkipCount = 0;
      this.historyCurrentPage = 1;
    });
  };

  async getAll(request: PagedMasterBuildRequest) {
    this.setLoading(true);
    try {
      const result = await MasterBuildService.getAll(request);
      runInAction(() => {
        this.builds.clear();
        result.items.forEach((x) => this.builds.set(x.id, x));
        this.totalCount = result.totalCount;
      });
    } catch (error) {
      console.error(error);
    }
    this.setLoading(false);
  }

  /**
   * Used by the table's export, which needs every row rather than the current page.
   * Deliberately does not touch the observable page.
   */
  async fetchAllForExport(request: PagedMasterBuildRequest): Promise<MasterBuild[]> {
    try {
      const result = await MasterBuildService.getAll({
        ...request,
        skipCount: 0,
        maxResultCount: 1000,
      });
      return result.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async get(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await MasterBuildService.get(entityDto);
      runInAction(() => {
        this.build = result;
      });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }

  async create(request: CreateMasterBuild) {
    this.setSaving(true);
    try {
      const result = await MasterBuildService.create(request);
      runInAction(() => {
        this.builds.set(result.id, result);
        this.totalCount += 1;
      });
      message.success({ content: 'Build created successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  async update(request: UpdateMasterBuild) {
    this.setSaving(true);
    try {
      const result = await MasterBuildService.update(request);
      this.applyResult(result);
      message.success({ content: 'Build updated successfully!' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  async delete(entityDto: EntityDto<string>) {
    this.setSaving(true);
    try {
      await MasterBuildService.delete(entityDto);
      runInAction(() => {
        this.builds.delete(entityDto.id);
        this.totalCount -= 1;
        if (this.build?.id === entityDto.id) this.build = undefined;
      });
      message.success({ content: 'Build deleted. Its jobs were kept.' });
    } catch (error) {
      console.error(error);
    } finally {
      this.setSaving(false);
    }
  }

  async addOrder(masterBuildId: string, order: CreateOrder) {
    this.setSaving(true);
    try {
      const result = await MasterBuildService.addOrder(masterBuildId, order);
      this.applyResult(result);
      message.success({ content: 'Job added to the build!' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  async deleteOrder(masterBuildId: string, orderId: string) {
    this.setSaving(true);
    try {
      const result = await MasterBuildService.deleteOrder(masterBuildId, orderId);
      this.applyResult(result);
      message.success({ content: 'Job deleted.' });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      this.setSaving(false);
    }
  }

  /**
   * Every write endpoint returns the whole rehydrated build, so both the open build and its row in
   * the table are refreshed from one response.
   */
  private applyResult(result: MasterBuild) {
    runInAction(() => {
      this.build = result;
      if (this.builds.has(result.id)) this.builds.set(result.id, result);
    });
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSaving = (value: boolean) => {
    runInAction(() => {
      this.saving = value;
    });
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };
}

export default MasterBuildStore;
